import { Component, Input, Output, EventEmitter, inject, OnChanges, SimpleChanges, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { FeatureCardComponent, FeatureCardData } from '../feature-card/feature-card.component';
import { ModelCardComponent, ModelCardData } from '../model-card/model-card.component';
import { UsecaseCardComponent, UsecaseCardData } from '../usecase-card/usecase-card.component';
import { UserInteractionApiService, CatalogueApiService } from '@angular-workspace/services';
import { FormatService } from '../../services/format.service';
import { HelperService } from '../../services/helper.service';
import { FeatureCardMapperService } from '../../services/feature-card-mapper.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { navigateToCatalogueAssetDetail } from '../../utils/catalogue-asset-detail-navigation.util';
import { CollectionListingStateService } from '../../services/collection-listing-state.service';

export type ViewMode = 'grid' | 'list' | 'table';
export type CollectionTab = 'all' | 'bookmarked' | 'liked';

@Component({
  selector: 'app-asset-grid',
  standalone: true,
  imports: [CommonModule, FormsModule, FeatureCardComponent, ModelCardComponent, UsecaseCardComponent],
  templateUrl: './asset-grid.component.html',
  styleUrl: './asset-grid.component.scss',
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: '0', overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: '1' }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: '1', overflow: 'hidden' }),
        animate('200ms ease-in', style({ height: '0', opacity: '0' }))
      ])
    ])
  ]
})
export class AssetGridComponent implements OnChanges, OnInit {
  @Input() assets: (FeatureCardData | ModelCardData | UsecaseCardData)[] = [];
  @Input() assetType: 'dataset' | 'model' | 'usecase' = 'dataset';
  @Input() searchPlaceholder: string = 'Search for assets';
  @Input() searchQuery: string = ''; // Changed from property to @Input
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 1;
  @Input() viewMode: ViewMode = 'grid';
  @Input() itemsPerPage: number = 6;
  @Input() showMobileFilterButton: boolean = false;
  /** When false, hide the Bookmarks and Like filter toggle buttons (e.g. on Challenges: New / Under Evaluation / Past). */
  @Input() showBookmarkLikeButtons: boolean = true;
  @Input() isLoggedIn: boolean = false;
  /** When returning from a detail page, restore bookmarked/liked tab selection. */
  @Input() initialCollectionTab: CollectionTab | null = null;
  @Output() pageChange = new EventEmitter<number>();
  @Output() viewModeChange = new EventEmitter<ViewMode>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() itemsPerPageChange = new EventEmitter<number>();
  @Output() openMobileFilter = new EventEmitter<void>();
  @Output() bookmarksClick = new EventEmitter<void>();
  @Output() likeClick = new EventEmitter<void>();
  @Output() sortChange = new EventEmitter<string>();
  /** Emits true when entering bookmark/like mode, false when exiting. */
  @Output() interactionModeChange = new EventEmitter<boolean>();
  @Output() collectionTabChange = new EventEmitter<CollectionTab>();

  @Input() sortOption: string = 'new-to-old';
  /** When set (e.g. Challenges Under Evaluation / Past), use these options instead of default. */
  @Input() customSortOptions: { value: string; label: string }[] | null = null;

  sortOptions: { value: string; label: string }[] = [
    { value: 'a-z', label: 'A-Z' },
    { value: 'z-a', label: 'Z-A' },
    { value: 'new-to-old', label: 'New to Old' },
    { value: 'old-to-new', label: 'Old to New' },
  ];

  get displaySortOptions(): { value: string; label: string }[] {
    return this.customSortOptions ?? this.sortOptions;
  }

  activeCollectionTab: CollectionTab = 'all';

  /** Tab to restore once login state is known. */
  private pendingCollectionTabRestore: CollectionTab | null = null;
  private lastRestoredCollectionTab: CollectionTab | null = null;

  get showCollectionTabs(): boolean {
    return (
      this.showBookmarkLikeButtons &&
      (this.assetType === 'dataset' || this.assetType === 'model')
    );
  }

  get isCollectionInteractionTab(): boolean {
    return this.showCollectionTabs && this.activeCollectionTab !== 'all';
  }

  /** Industry column in table view for datasets and models. */
  get showIndustryInTable(): boolean {
    return this.assetType === 'dataset' || this.assetType === 'model';
  }

  /** Colspan for expandable table rows (toggle + data columns). */
  get tableColumnCount(): number {
    if (this.assetType === 'usecase') {
      return 0;
    }

    let count = 4; // title, permission, action + one type-specific column
    if (this.assetType === 'dataset') {
      count += 2; // organization + industry
    } else if (this.assetType === 'model') {
      count += 1; // industry
    }

    return count + 1; // expand toggle column
  }

  get bookmarksActive(): boolean {
    return this.activeCollectionTab === 'bookmarked';
  }

  get likeActive(): boolean {
    return this.activeCollectionTab === 'liked';
  }

  get primaryCollectionTabLabel(): string {
    if (this.assetType === 'dataset') return 'Datasets';
    if (this.assetType === 'model') return 'Models';
    return 'All';
  }

  readonly collectionTabs: { id: CollectionTab; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'bookmarked', label: 'Bookmarked' },
    { id: 'liked', label: 'Liked' },
  ];
  bookmarkedItemIds: string[] = [];
  bookmarksTotalCount: number = 0;
  likedItemIds: string[] = [];
  likesTotalCount: number = 0;
  filteredAssetsFromSearch: (FeatureCardData | ModelCardData | UsecaseCardData)[] = [];
  filteredTotalPages: number = 1; // Track totalPages from search API when filters are active
  _filteredAssets: (FeatureCardData | ModelCardData | UsecaseCardData)[] = []; // Cached filtered assets for better change detection

  private router = inject(Router);
  private userInteractionApiService = inject(UserInteractionApiService);
  private catalogueApiService = inject(CatalogueApiService);
  private formatService = inject(FormatService);
  private helperService = inject(HelperService);
  private featureCardMapper = inject(FeatureCardMapperService);
  private googleAnalytics = inject(GoogleAnalyticsService);
  private listingState = inject(CollectionListingStateService);
  private cdr = inject(ChangeDetectorRef);
  itemsPerPageOptions: number[] = [6, 12, 24, 48];
  /** At most one table "more details" row expanded at a time. */
  expandedRowId: string | null = null;

  /** Legacy template map shape — only the active row id is true when expanded. */
  get expandedRowKeys(): Record<string, boolean> {
    return this.expandedRowId ? { [this.expandedRowId]: true } : {};
  }
  sortDropdownOpen = false;
  showDropdownOpen = false;
  private interactionRefreshInFlight = false;
  private readonly interactionPageSize = 8;

  /** Page size for bookmark / like user-interactions API requests. */
  private getInteractionPageSize(): number {
    return this.bookmarksActive || this.likeActive ? this.interactionPageSize : this.itemsPerPage;
  }

  ngOnInit(): void {
    if (this.isLoggedIn) {
      void this.loadLikedItemIds();
    }
    this.tryRestoreCollectionTab();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialCollectionTab'] || changes['isLoggedIn']) {
      this.tryRestoreCollectionTab();
    }

    if (changes['assets'] || changes['currentPage']) {
      this.collapseExpandedRows();
    }

    const interactionFiltersActive = this.bookmarksActive || this.likeActive;
    const itemsPerPageChanged = !!changes['itemsPerPage'];
    const currentPageChanged = !!changes['currentPage'];

    // When interaction filters are active, "Showing" and pagination must re-fetch
    // interaction-filtered results; parent API updates don't update `filteredAssetsFromSearch`.
    if (
      interactionFiltersActive &&
      this.isLoggedIn &&
      (itemsPerPageChanged || currentPageChanged)
    ) {
      void this.refreshInteractionFilteredAssets();
      return;
    }

    // If assets change and filters are active, keep filtered view.
    if (changes['assets'] && interactionFiltersActive) {
      // No need to re-fetch - interaction-filtered list drives the UI.
    }

    // Refresh liked IDs when the user logs in or when the catalogue list is (re)loaded
    // (e.g. returning from a detail page after liking an asset).
    if (this.isLoggedIn && !interactionFiltersActive) {
      if (changes['isLoggedIn']?.currentValue === true) {
        void this.loadLikedItemIds();
      } else if (changes['assets']) {
        const prevLen = changes['assets'].previousValue?.length ?? 0;
        const currLen = this.assets?.length ?? 0;
        if (currLen > 0 && (prevLen === 0 || changes['assets'].firstChange)) {
          void this.loadLikedItemIds();
        }
      }
    }
  }

  /**
   * Silently fetches the current user's liked asset IDs and stores them in
   * `likedItemIds` so that card heart icons reflect the correct liked state
   * on page load — without activating the Like filter or altering the asset list.
   */
  private async loadLikedItemIds(): Promise<void> {
    if (!this.isLoggedIn) return;
    try {
      const assetType = this.getInteractionAssetTypeFilter();
      const likedIds = new Set<string>();
      let page = 1;
      let totalPages = 1;
      const pageSize = 200;

      do {
        const response = await this.userInteractionApiService.userInteractions({
          actionType: 'LIKE',
          page,
          size: pageSize,
          ...(assetType ? { assetType } : {}),
        });

        const items = this.extractInteractionItems(response);
        for (const item of items) {
          const id = this.extractInteractionAssetId(item);
          if (id) {
            likedIds.add(id);
          }
        }

        totalPages = response?.paginationInfo?.totalPages ?? 1;
        page++;
      } while (page <= totalPages);

      this.likedItemIds = Array.from(likedIds);
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch {
      // Non-critical — silently ignore; hearts will simply remain unfilled.
    }
  }

  /** API assetType for user-interactions queries (matches detail pages). */
  private getInteractionAssetTypeFilter(): string | undefined {
    if (this.assetType === 'dataset') return 'DATABANK';
    if (this.assetType === 'model') return 'AI_MODEL';
    if (this.assetType === 'usecase') return 'USECASE';
    return undefined;
  }

  /** Normalized asset id from a user-interactions list item. */
  private extractInteractionAssetId(item: any): string {
    if (!item || typeof item !== 'object') return '';
    const asset = this.getAssetFromInteractionItem(item);
    const candidates = asset
      ? [
          asset.id,
          asset.itemId,
          asset.assetId,
          asset.entityId,
          asset.entity_id,
          item.assetId,
          item.itemId,
          item.id,
          item.entityId,
          item.entity_id,
        ]
      : [
          item.assetId,
          item.itemId,
          item.id,
          item.entityId,
          item.entity_id,
        ];
    for (const value of candidates) {
      if (value !== null && value !== undefined && value !== '') {
        return String(value);
      }
    }
    return '';
  }

  private extractInteractionItems(response: any): any[] {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (response.result && Array.isArray(response.result)) return response.result;
    if (response.results && Array.isArray(response.results)) return response.results;
    if (response.data && Array.isArray(response.data)) return response.data;
    return [];
  }

  /** Catalogue sort string for searchAssetsByIds (matches models/datasets listing). */
  private getCatalogueSortForSearchByIds(): string {
    switch (this.sortOption) {
      case 'a-z':
        return 'name:asc';
      case 'z-a':
        return 'name:desc';
      case 'old-to-new':
        return 'itemCreatedAt:asc';
      case 'new-to-old':
      default:
        return 'itemCreatedAt:desc';
    }
  }

  /** Keep only interaction rows for the current collection asset type (e.g. models vs datasets). */
  private filterInteractionItemsForCurrentAssetType(items: any[]): any[] {
    const patterns: Record<'dataset' | 'model' | 'usecase', string[]> = {
      dataset: ['databank', 'dataset'],
      model: ['aimodel', 'ai_model', 'model'],
      usecase: ['usecase', 'apps', 'app'],
    };
    const allowed = patterns[this.assetType];
    return items.filter((item) => {
      const asset = this.getAssetFromInteractionItem(item) ?? item;
      const type = String(asset?.type ?? asset?.['@type'] ?? '').toLowerCase();
      if (!type) {
        return true;
      }
      return allowed.some((p) => type.includes(p));
    });
  }

  /** Preserve interaction list order after catalogue search-by-ids. */
  private orderCatalogueItemsByIds(items: any[], orderedIds: string[]): any[] {
    const byId = new Map<string, any>();
    for (const item of items) {
      const id = item?.id != null ? String(item.id) : '';
      if (id) {
        byId.set(id, item);
      }
    }
    return orderedIds.map((id) => byId.get(String(id))).filter((item): item is any => item != null);
  }

  /**
   * Refresh BOOKMARK / LIKE filtered list using pagination inputs.
   * This is required because the UI renders from `filteredAssetsFromSearch`
   * (not from parent `assets`) while the toggle filters are active.
   */
  private async refreshInteractionFilteredAssets(): Promise<void> {
    if (!this.isLoggedIn) return;
    if (this.interactionRefreshInFlight) return;

    this.interactionRefreshInFlight = true;
    try {
      const actionType = this.bookmarksActive ? 'BOOKMARK' : 'LIKE';

      // Clear immediately so the user doesn't see stale results.
      this.collapseExpandedRows();
      this.filteredAssetsFromSearch = [];
      this._filteredAssets = [];
      this.filteredTotalPages = 1;

      const interactionAssetType = this.getInteractionAssetTypeFilter();
      const response = await this.userInteractionApiService.userInteractions({
        actionType,
        page: this.currentPage,
        size: this.getInteractionPageSize(),
        ...(interactionAssetType ? { assetType: interactionAssetType } : {}),
      });

      if (!response || !response.paginationInfo) {
        if (this.bookmarksActive) {
          this.bookmarksTotalCount = 0;
          this.bookmarkedItemIds = [];
        } else {
          this.likesTotalCount = 0;
          this.likedItemIds = [];
        }
        this.cdr.detectChanges();
        return;
      }

      const totalCount = response.paginationInfo.totalCount || 0;
      const paginationTotalPages = response.paginationInfo.totalPages;

      let items = this.extractInteractionItems(response);
      items = this.filterInteractionItemsForCurrentAssetType(items);

      if (items.length === 0) {
        if (this.bookmarksActive) {
          this.bookmarksTotalCount = 0;
          this.bookmarkedItemIds = [];
        } else {
          this.likesTotalCount = 0;
          this.likedItemIds = [];
        }
        this.cdr.detectChanges();
        return;
      }

      const assetTypeDisplay =
        this.assetType === 'dataset'
          ? 'Dataset'
          : this.assetType === 'model'
            ? 'Model'
            : this.assetType === 'usecase'
              ? 'Use Case'
              : undefined;

      // Extract asset IDs for ribbons / bookmark / like state
      const assetIds = items
        .map((item: any) => this.extractInteractionAssetId(item))
        .filter((id: string) => id);

      if (this.bookmarksActive) {
        this.bookmarksTotalCount = totalCount;
        this.bookmarkedItemIds = assetIds;
      } else {
        this.likesTotalCount = totalCount;
        this.likedItemIds = assetIds;
      }

      // Always load full catalogue records by ID so cards match the default listing
      // (interaction payloads are often partial — e.g. sortDescription only, no modelType).
      if (assetIds.length > 0 && assetTypeDisplay) {
        const searchResponse = await this.catalogueApiService.searchAssetsByIds(
          assetIds,
          assetTypeDisplay,
          1,
          assetIds.length,
          this.getCatalogueSortForSearchByIds()
        );

        const searchItems = this.orderCatalogueItemsByIds(
          this.extractItemsFromResponse(searchResponse),
          assetIds
        );

        if (searchResponse?.paginationInfo?.totalPages !== undefined) {
          this.filteredTotalPages =
            paginationTotalPages ?? (searchResponse.paginationInfo.totalPages || 1);
        } else {
          this.filteredTotalPages =
            paginationTotalPages ??
            (Math.ceil((totalCount || searchItems.length) / this.getInteractionPageSize()) || 1);
        }

        this.filteredAssetsFromSearch = searchItems
          .map((item, index) => {
            try {
              return this.mapApiItemToCardData(item);
            } catch (e) {
              console.error(`Error mapping item ${index} (${actionType}):`, e);
              return null;
            }
          })
          .filter(
            (item): item is FeatureCardData | ModelCardData | UsecaseCardData => item !== null
          );
      } else {
        this.filteredAssetsFromSearch = [];
        this.filteredTotalPages = paginationTotalPages ?? 1;
      }

      this._filteredAssets = [...this.filteredAssetsFromSearch];
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      console.error(`Failed to refresh ${this.bookmarksActive ? 'bookmarks' : 'likes'}:`, error);
      this.filteredAssetsFromSearch = [];
      this._filteredAssets = [];
      this.filteredTotalPages = 1;
      if (this.bookmarksActive) {
        this.bookmarksTotalCount = 0;
        this.bookmarkedItemIds = [];
      } else {
        this.likesTotalCount = 0;
        this.likedItemIds = [];
      }
      this.cdr.detectChanges();
    } finally {
      this.interactionRefreshInFlight = false;

      // Keep existing behavior: emit click events so parent can open login modal if needed.
      if (this.bookmarksActive) this.bookmarksClick.emit();
      if (this.likeActive) this.likeClick.emit();
    }
  }

  /**
   * Load bookmarked items to show ribbons on cards
   */
  async loadBookmarkedItems(): Promise<void> {
    if (!this.isLoggedIn) {
      return;
    }

    try {
      const interactionAssetType = this.getInteractionAssetTypeFilter();
      const response = await this.userInteractionApiService.userInteractions({
        actionType: 'BOOKMARK',
        ...(interactionAssetType ? { assetType: interactionAssetType } : {}),
      });
      console.log('Bookmarks API response:', response);

      if (response && response.paginationInfo) {
        this.bookmarksTotalCount = response.paginationInfo.totalCount || 0;

        // Extract item IDs from the response - check both 'result' and 'results'
        let items: any[] = [];
        if (response.result && Array.isArray(response.result)) {
          items = response.result;
        } else if (response.results && Array.isArray(response.results)) {
          items = response.results;
        } else if (response.data && Array.isArray(response.data)) {
          items = response.data;
        }

        if (items.length > 0) {
          this.bookmarkedItemIds = items.map((item: any) => {
            // Try multiple possible ID fields
            return item.itemId || item.id || item.entityId || item.entity_id || item.assetId || '';
          }).filter((id: string) => id);
          console.log('Extracted bookmarked item IDs:', this.bookmarkedItemIds);
        } else {
          this.bookmarkedItemIds = [];
        }
      } else {
        this.bookmarksTotalCount = 0;
        this.bookmarkedItemIds = [];
      }
    } catch (error) {
      console.error('Error loading bookmarked items:', error);
      // Silently fail - don't show ribbons if API fails
      this.bookmarkedItemIds = [];
      this.bookmarksTotalCount = 0;
    }
  }

  /**
   * Get filtered assets based on bookmarks or likes state
   */
  get filteredAssets(): (FeatureCardData | ModelCardData | UsecaseCardData)[] {
    // If neither filter is active, show all assets
    if (!this.bookmarksActive && !this.likeActive) {
      this._filteredAssets = this.assets || [];
      return this._filteredAssets;
    }

    // If filter is active but user is not logged in, show all assets
    if (!this.isLoggedIn) {
      this._filteredAssets = this.assets || [];
      return this._filteredAssets;
    }

    // If bookmarks or likes are active, ONLY use assets from search API
    // Do not fallback to filtering existing assets - this ensures we only show
    // the exact results from the API response
    if (this.bookmarksActive || this.likeActive) {
      // Return only the assets from the search API response
      this._filteredAssets = this.filteredAssetsFromSearch || [];
      console.log('filteredAssets getter - bookmarksActive:', this.bookmarksActive, 'likeActive:', this.likeActive, 'filteredAssetsFromSearch length:', this.filteredAssetsFromSearch.length, '_filteredAssets length:', this._filteredAssets.length, 'returning:', this._filteredAssets.length, 'items');
      return this._filteredAssets;
    }

    this._filteredAssets = this.assets || [];
    return this._filteredAssets;
  }

  trackByAssetId(_index: number, asset: FeatureCardData | ModelCardData | UsecaseCardData): string {
    return asset.id;
  }

  getEmptyStateMessage(): string {
    const assetTypeName = this.assetType === 'dataset' ? 'Datasets' :
      this.assetType === 'model' ? 'Models' :
        'Use Cases';

    if (this.bookmarksActive && this.isLoggedIn) {
      return `No bookmarked ${assetTypeName.toLowerCase()} found.`;
    }

    if (this.likeActive && this.isLoggedIn) {
      return `No liked ${assetTypeName.toLowerCase()} found.`;
    }

    return `No ${assetTypeName} found. Please try with a new search`;
  }

  /**
   * Check if an asset is bookmarked
   */
  isAssetBookmarked(assetId: string | undefined): boolean {
    if (!assetId || !this.isLoggedIn) {
      return false;
    }
    const id = String(assetId);
    return this.bookmarkedItemIds.some((bookmarkedId) => String(bookmarkedId) === id);
  }

  /**
   * Check if an asset is liked
   */
  isAssetLiked(assetId: string | undefined): boolean {
    if (!assetId || !this.isLoggedIn) {
      return false;
    }
    const id = String(assetId);
    return this.likedItemIds.some((likedId) => String(likedId) === id);
  }

  /**
   * Handle bookmark state change from feature card
   */
  onBookmarkChanged(assetId: string | undefined, isBookmarked: boolean): void {
    if (!assetId) return;

    if (isBookmarked) {
      // Add to bookmarked items if not already present
      if (!this.bookmarkedItemIds.includes(assetId)) {
        this.bookmarkedItemIds.push(assetId);
        this.bookmarksTotalCount++;
      }
    } else {
      // Remove from bookmarked items
      const index = this.bookmarkedItemIds.indexOf(assetId);
      if (index > -1) {
        this.bookmarkedItemIds.splice(index, 1);
        this.bookmarksTotalCount = Math.max(0, this.bookmarksTotalCount - 1);
      }
    }
  }

  /**
   * Handle like state change from feature card
   */
  onLikeChanged(assetId: string | undefined, isLiked: boolean): void {
    if (!assetId) return;

    if (isLiked) {
      // Add to liked items if not already present
      if (!this.likedItemIds.includes(assetId)) {
        this.likedItemIds.push(assetId);
        this.likesTotalCount++;
      }
    } else {
      // Remove from liked items
      const index = this.likedItemIds.indexOf(assetId);
      if (index > -1) {
        this.likedItemIds.splice(index, 1);
        this.likesTotalCount = Math.max(0, this.likesTotalCount - 1);
      }
    }
  }

  toggleExpansion(assetId: string): void {
    if (!assetId) {
      return;
    }
    this.expandedRowId = this.expandedRowId === assetId ? null : assetId;
    this.cdr.markForCheck();
  }

  isRowExpanded(assetId: string): boolean {
    return !!assetId && this.expandedRowId === assetId;
  }

  private collapseExpandedRows(): void {
    if (this.expandedRowId === null) {
      return;
    }
    this.expandedRowId = null;
    this.cdr.markForCheck();
  }

  onSearchChange(): void {
    if (this.bookmarksActive || this.likeActive) return;
    this.searchChange.emit(this.searchQuery);
  }

  onPageChange(page: number): void {
    const effectiveTotalPages = this.getEffectiveTotalPages();
    if (page >= 1 && page <= effectiveTotalPages) {
      this.collapseExpandedRows();
      this.pageChange.emit(page);
      // Scroll to top so the user sees the new page content
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  onViewModeChange(mode: ViewMode): void {
    if (this.viewMode === 'table' && mode !== 'table') {
      this.collapseExpandedRows();
    }
    this.viewModeChange.emit(mode);
  }

  /** Reset grid/list/table view when switching Datasets / Bookmarked / Liked tabs. */
  private resetCollectionViewState(): void {
    this.collapseExpandedRows();
    this.sortDropdownOpen = false;
    this.showDropdownOpen = false;
    if (this.viewMode !== 'grid') {
      this.viewModeChange.emit('grid');
    }
    this.cdr.markForCheck();
  }

  onItemsPerPageChange(value: number): void {
    this.itemsPerPage = value;
    this.itemsPerPageChange.emit(value);
  }

  onSortChange(value: string): void {
    this.sortOption = value;
    this.sortChange.emit(value);
  }

  getSelectedSortLabel(): string {
    return this.displaySortOptions.find(o => o.value === this.sortOption)?.label || 'New to Old';
  }

  getSelectedShowLabel(): string {
    return String(this.itemsPerPage);
  }

  toggleSortDropdown(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.sortDropdownOpen = !this.sortDropdownOpen;
    if (this.sortDropdownOpen) this.showDropdownOpen = false;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  toggleShowDropdown(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.showDropdownOpen = !this.showDropdownOpen;
    if (this.showDropdownOpen) this.sortDropdownOpen = false;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  selectSortOption(value: string, event?: Event): void {
    event?.stopPropagation();
    this.sortOption = value;
    this.sortChange.emit(value);
    this.sortDropdownOpen = false;
    this.cdr.markForCheck();
  }

  selectShowOption(value: number, event?: Event): void {
    event?.stopPropagation();
    this.itemsPerPage = value;
    this.itemsPerPageChange.emit(value);
    this.showDropdownOpen = false;
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  closeDropdownsOnOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.asset-grid--custom-dropdown')) return;
    // Defer close to next tick to avoid racing with toggle (change detection)
    setTimeout(() => {
      this.sortDropdownOpen = false;
      this.showDropdownOpen = false;
      this.cdr.markForCheck();
    }, 0);
  }

  onMobileFilterClick(): void {
    console.log('Mobile filter button clicked!');
    console.log('showMobileFilterButton:', this.showMobileFilterButton);
    console.log('openMobileFilter emitter:', this.openMobileFilter);
    this.openMobileFilter.emit();
    console.log('Event emitted');
  }

  getCollectionTabLabel(tab: { id: CollectionTab; label: string }): string {
    return tab.id === 'all' ? this.primaryCollectionTabLabel : tab.label;
  }

  isCollectionTabLocked(tab: CollectionTab): boolean {
    return (tab === 'bookmarked' || tab === 'liked') && !this.isLoggedIn;
  }

  /** Restore bookmarked/liked tab after returning from a detail page without resetting pagination. */
  async restoreCollectionTab(tab: CollectionTab): Promise<void> {
    if (!this.showCollectionTabs || tab === 'all') {
      return;
    }

    this.syncInteractionTabUi(tab);

    if ((tab === 'bookmarked' || tab === 'liked') && !this.isLoggedIn) {
      this.pendingCollectionTabRestore = tab;
      return;
    }

    this.pendingCollectionTabRestore = null;
    this.lastRestoredCollectionTab = tab;
    await this.loadInteractionTabData(tab);
  }

  /** Apply saved collection tab from parent when returning from a detail page. */
  private tryRestoreCollectionTab(): void {
    const tab = this.initialCollectionTab;
    if (!tab || tab === 'all' || !this.showCollectionTabs) {
      this.lastRestoredCollectionTab = null;
      return;
    }

    this.syncInteractionTabUi(tab);

    if ((tab === 'bookmarked' || tab === 'liked') && !this.isLoggedIn) {
      this.pendingCollectionTabRestore = tab;
      return;
    }

    const hadPendingLogin = this.pendingCollectionTabRestore !== null;
    this.pendingCollectionTabRestore = null;

    if (
      !hadPendingLogin &&
      this.lastRestoredCollectionTab === tab &&
      this.filteredAssetsFromSearch.length > 0
    ) {
      return;
    }

    this.lastRestoredCollectionTab = tab;
    void this.loadInteractionTabData(tab);
  }

  /** Immediately switch grid UI to bookmarked/liked mode (hides search and showing controls). */
  private syncInteractionTabUi(tab: CollectionTab): void {
    if (this.activeCollectionTab !== tab) {
      this.activeCollectionTab = tab;
      this.collectionTabChange.emit(tab);
    }
    this.interactionModeChange.emit(true);
    this.cdr.markForCheck();
  }

  private async loadInteractionTabData(tab: CollectionTab): Promise<void> {
    this.collapseExpandedRows();
    this.sortDropdownOpen = false;
    this.showDropdownOpen = false;

    if (tab === 'bookmarked') {
      await this.activateBookmarkMode();
    } else if (tab === 'liked') {
      await this.activateLikeMode();
    }
  }

  async setCollectionTab(tab: CollectionTab): Promise<void> {
    if (this.showCollectionTabs) {
      if (tab === this.activeCollectionTab) return;

      if ((tab === 'bookmarked' || tab === 'liked') && !this.isLoggedIn) {
        if (tab === 'bookmarked') this.bookmarksClick.emit();
        else this.likeClick.emit();
        return;
      }

      this.resetCollectionViewState();

      const wasInteraction = this.isCollectionInteractionTab;
      this.activeCollectionTab = tab;
      this.collectionTabChange.emit(tab);

      if (tab === 'all') {
        this.lastRestoredCollectionTab = null;
        this.bookmarksTotalCount = 0;
        this.filteredAssetsFromSearch = [];
        this._filteredAssets = [];
        this.filteredTotalPages = 1;
        this.interactionModeChange.emit(false);
        if (wasInteraction) {
          this.pageChange.emit(1);
        }
        void this.loadLikedItemIds();
        return;
      }

      this.interactionModeChange.emit(true);
      this.pageChange.emit(1);
      this.filteredAssetsFromSearch = [];
      this._filteredAssets = [];

      if (tab === 'bookmarked') {
        await this.activateBookmarkMode();
      } else {
        await this.activateLikeMode();
      }
      this.lastRestoredCollectionTab = tab;
      return;
    }

    if (tab === 'bookmarked') {
      await this.toggleBookmarks();
    } else if (tab === 'liked') {
      await this.toggleLike();
    }
  }

  private async activateBookmarkMode(): Promise<void> {
    this.likesTotalCount = 0;
    this.filteredAssetsFromSearch = [];
    this.filteredTotalPages = 1;

    try {
      const interactionAssetType = this.getInteractionAssetTypeFilter();
      const response = await this.userInteractionApiService.userInteractions({
        actionType: 'BOOKMARK',
        page: this.currentPage,
        size: this.getInteractionPageSize(),
        ...(interactionAssetType ? { assetType: interactionAssetType } : {}),
      });

      // Extract bookmarked item IDs and total count from response
      if (response && response.paginationInfo) {
        this.bookmarksTotalCount = response.paginationInfo.totalCount || 0;

        // Extract item IDs from the response - check multiple possible response structures
        let items: any[] = [];
        if (response.result && Array.isArray(response.result)) {
          items = response.result;
        } else if (response.results && Array.isArray(response.results)) {
          items = response.results;
        } else if (response.data && Array.isArray(response.data)) {
          items = response.data;
        }

        if (items.length > 0) {
          // Extract asset IDs for bookmark ribbons
          const assetIds = items
            .map((item: any) => this.extractInteractionAssetId(item))
            .filter((id: string) => id);
          this.bookmarkedItemIds = assetIds;

          // Use asset information from interaction response when available (no item API call)
          const assetsFromInteraction = items
            .map((item: any) => this.getAssetFromInteractionItem(item))
            .filter((a: any) => a != null) as any[];

          if (assetsFromInteraction.length > 0) {
            // Interaction API returned full asset info – map directly to cards
            try {
              this.filteredTotalPages = response.paginationInfo?.totalPages ?? (Math.ceil((response.paginationInfo?.totalCount ?? assetsFromInteraction.length) / this.getInteractionPageSize()) || 1);
              this.filteredAssetsFromSearch = assetsFromInteraction.map((item, index) => {
                try {
                  return this.mapApiItemToCardData(item);
                } catch (error) {
                  console.error(`Error mapping interaction asset ${index}:`, error, 'Item:', item);
                  return null;
                }
              }).filter((item): item is FeatureCardData | ModelCardData | UsecaseCardData => item !== null);
              this._filteredAssets = [...this.filteredAssetsFromSearch];
              this.cdr.markForCheck();
              this.cdr.detectChanges();
              setTimeout(() => { this.cdr.markForCheck(); this.cdr.detectChanges(); }, 0);
            } catch (error) {
              console.error('Error mapping interaction assets to card data:', error);
              this.filteredAssetsFromSearch = [];
              this._filteredAssets = [];
              this.filteredTotalPages = 1;
              this.cdr.detectChanges();
            }
          } else {
            // Fallback: call search API by IDs when interaction does not include asset details
            if (assetIds.length > 0) {
              try {
                const assetTypeDisplay = this.assetType === 'dataset' ? 'Dataset' : this.assetType === 'model' ? 'Model' : this.assetType === 'usecase' ? 'Use Case' : undefined;
                const searchResponse = await this.catalogueApiService.searchAssetsByIds(assetIds, assetTypeDisplay, this.currentPage, this.getInteractionPageSize());
                const searchItems = this.extractItemsFromResponse(searchResponse);
                if (searchResponse?.paginationInfo) {
                  this.filteredTotalPages = searchResponse.paginationInfo.totalPages || 1;
                } else {
                  const totalCount = searchResponse?.paginationInfo?.totalCount || searchItems?.length || 0;
                  this.filteredTotalPages = Math.ceil(totalCount / this.getInteractionPageSize()) || 1;
                }
                this.filteredAssetsFromSearch = searchItems.map((item, index) => {
                  try { return this.mapApiItemToCardData(item); } catch (e) { console.error(`Error mapping item ${index}:`, e); return null; }
                }).filter(item => item !== null) as (FeatureCardData | ModelCardData | UsecaseCardData)[];
                this._filteredAssets = [...this.filteredAssetsFromSearch];
                this.cdr.markForCheck();
                this.cdr.detectChanges();
                setTimeout(() => { this.cdr.markForCheck(); this.cdr.detectChanges(); }, 0);
              } catch (error) {
                console.error('Error fetching bookmarked assets:', error);
                this.filteredAssetsFromSearch = [];
                this.filteredTotalPages = 1;
              }
            } else {
              this.filteredAssetsFromSearch = [];
            }
          }
        } else {
          this.bookmarkedItemIds = [];
          this.filteredAssetsFromSearch = [];
          this.filteredTotalPages = 1;
          this.cdr.detectChanges();
        }
      } else {
        this.bookmarksTotalCount = 0;
        this.bookmarkedItemIds = [];
        this.filteredAssetsFromSearch = [];
        this.filteredTotalPages = 1;
        this.cdr.detectChanges();
      }

      this.bookmarksClick.emit();
    } catch (error) {
      console.error('Error calling userInteractions API:', error);
      this.bookmarksTotalCount = 0;
      this.bookmarkedItemIds = [];
      this.activeCollectionTab = 'all';
      this.collectionTabChange.emit('all');
      this.interactionModeChange.emit(false);
      this.bookmarksClick.emit();
    }
  }

  async toggleBookmarks(): Promise<void> {
    if (!this.isLoggedIn) {
      this.bookmarksClick.emit();
      return;
    }

    if (this.bookmarksActive) {
      if (this.showCollectionTabs) {
        await this.setCollectionTab('all');
      } else {
        this.activeCollectionTab = 'all';
        this.bookmarksTotalCount = 0;
        this.bookmarkedItemIds = [];
        this.filteredAssetsFromSearch = [];
        this.filteredTotalPages = 1;
        this.interactionModeChange.emit(false);
        this.bookmarksClick.emit();
      }
      return;
    }

    if (this.showCollectionTabs) {
      await this.setCollectionTab('bookmarked');
      return;
    }

    this.activeCollectionTab = 'bookmarked';
    this.interactionModeChange.emit(true);
    this.filteredAssetsFromSearch = [];
    await this.activateBookmarkMode();
  }

  private async activateLikeMode(): Promise<void> {
    this.bookmarksTotalCount = 0;
    this.bookmarkedItemIds = [];
    this.filteredAssetsFromSearch = [];
    this.filteredTotalPages = 1;

    try {
      const interactionAssetType = this.getInteractionAssetTypeFilter();
      const response = await this.userInteractionApiService.userInteractions({
        actionType: 'LIKE',
        page: this.currentPage,
        size: this.getInteractionPageSize(),
        ...(interactionAssetType ? { assetType: interactionAssetType } : {}),
      });

      // Extract liked item IDs and total count from response
      if (response && response.paginationInfo) {
        this.likesTotalCount = response.paginationInfo.totalCount || 0;

        // Extract item IDs from the response - check multiple possible response structures
        let items: any[] = [];
        if (response.result && Array.isArray(response.result)) {
          items = response.result;
        } else if (response.results && Array.isArray(response.results)) {
          items = response.results;
        } else if (response.data && Array.isArray(response.data)) {
          items = response.data;
        }

        if (items.length > 0) {
          // Extract asset IDs for like ribbons
          const assetIds = items
            .map((item: any) => this.extractInteractionAssetId(item))
            .filter((id: string) => id);
          this.likedItemIds = assetIds;

          // Use asset information from interaction response when available (no item API call)
          const assetsFromInteraction = items
            .map((item: any) => this.getAssetFromInteractionItem(item))
            .filter((a: any) => a != null) as any[];

          if (assetsFromInteraction.length > 0) {
            // Interaction API returned full asset info – map directly to cards
            try {
              this.filteredTotalPages = response.paginationInfo?.totalPages ?? (Math.ceil((response.paginationInfo?.totalCount ?? assetsFromInteraction.length) / this.getInteractionPageSize()) || 1);
              this.filteredAssetsFromSearch = assetsFromInteraction.map((item, index) => {
                try {
                  return this.mapApiItemToCardData(item);
                } catch (error) {
                  console.error(`Error mapping interaction asset ${index} for likes:`, error, 'Item:', item);
                  return null;
                }
              }).filter((item): item is FeatureCardData | ModelCardData | UsecaseCardData => item !== null);
              this._filteredAssets = [...this.filteredAssetsFromSearch];
              this.cdr.markForCheck();
              this.cdr.detectChanges();
              setTimeout(() => { this.cdr.markForCheck(); this.cdr.detectChanges(); }, 0);
            } catch (error) {
              console.error('Error mapping interaction assets to card data for likes:', error);
              this.filteredAssetsFromSearch = [];
              this._filteredAssets = [];
              this.filteredTotalPages = 1;
              this.cdr.detectChanges();
            }
          } else {
            // Fallback: call search API by IDs when interaction does not include asset details
            if (assetIds.length > 0) {
              try {
                const assetTypeDisplay = this.assetType === 'dataset' ? 'Dataset' : this.assetType === 'model' ? 'Model' : this.assetType === 'usecase' ? 'Use Case' : undefined;
                const searchResponse = await this.catalogueApiService.searchAssetsByIds(assetIds, assetTypeDisplay, this.currentPage, this.getInteractionPageSize());
                const searchItems = this.extractItemsFromResponse(searchResponse);
                if (searchResponse?.paginationInfo) {
                  this.filteredTotalPages = searchResponse.paginationInfo.totalPages || 1;
                } else {
                  const totalCount = searchResponse?.paginationInfo?.totalCount || searchItems?.length || 0;
                  this.filteredTotalPages = Math.ceil(totalCount / this.getInteractionPageSize()) || 1;
                }
                this.filteredAssetsFromSearch = searchItems.map((item, index) => {
                  try { return this.mapApiItemToCardData(item); } catch (e) { console.error(`Error mapping item ${index} for likes:`, e); return null; }
                }).filter(item => item !== null) as (FeatureCardData | ModelCardData | UsecaseCardData)[];
                this._filteredAssets = [...this.filteredAssetsFromSearch];
                this.cdr.markForCheck();
                this.cdr.detectChanges();
                setTimeout(() => { this.cdr.markForCheck(); this.cdr.detectChanges(); }, 0);
              } catch (error) {
                console.error('Error fetching liked assets:', error);
                this.filteredAssetsFromSearch = [];
                this.filteredTotalPages = 1;
              }
            } else {
              this.filteredAssetsFromSearch = [];
            }
          }
        } else {
          this.likedItemIds = [];
          this.filteredAssetsFromSearch = [];
          this.filteredTotalPages = 1;
          this.cdr.detectChanges();
        }
      } else {
        this.likesTotalCount = 0;
        this.likedItemIds = [];
        this.filteredAssetsFromSearch = [];
        this.filteredTotalPages = 1;
        this.cdr.detectChanges();
      }

      this.likeClick.emit();
    } catch (error) {
      console.error('Error calling userInteractions API for likes:', error);
      this.likesTotalCount = 0;
      this.likedItemIds = [];
      this.activeCollectionTab = 'all';
      this.collectionTabChange.emit('all');
      this.interactionModeChange.emit(false);
      this.likeClick.emit();
    }
  }

  async toggleLike(): Promise<void> {
    if (!this.isLoggedIn) {
      this.likeClick.emit();
      return;
    }

    if (this.likeActive) {
      if (this.showCollectionTabs) {
        await this.setCollectionTab('all');
      } else {
        this.activeCollectionTab = 'all';
        this.likesTotalCount = 0;
        this.filteredAssetsFromSearch = [];
        this.filteredTotalPages = 1;
        void this.loadLikedItemIds();
        this.interactionModeChange.emit(false);
        this.likeClick.emit();
      }
      return;
    }

    if (this.showCollectionTabs) {
      await this.setCollectionTab('liked');
      return;
    }

    this.activeCollectionTab = 'liked';
    this.interactionModeChange.emit(true);
    this.filteredAssetsFromSearch = [];
    await this.activateLikeMode();
  }

  /**
   * Get effective total pages based on whether filters are active
   */
  getEffectiveTotalPages(): number {
    // If bookmarks or likes are active, use filteredTotalPages
    if (this.bookmarksActive || this.likeActive) {
      return this.filteredTotalPages;
    }
    // Otherwise, use the regular totalPages from parent
    return this.totalPages;
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const effectiveTotalPages = this.getEffectiveTotalPages();
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(effectiveTotalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  /**
   * Check if badge color is restricted
   */
  isRestrictedBadge(badgeColor?: string): boolean {
    return badgeColor === 'restricted';
  }

  /**
   * Check if badge color is public
   */
  isPublicBadge(badgeColor?: string): boolean {
    return badgeColor === 'public';
  }

  /**
   * Navigate to asset detail page when action button is clicked
   */
  onActionClick(asset: FeatureCardData | ModelCardData | UsecaseCardData): void {
    if (!asset.id) {
      console.warn('Asset ID is missing, cannot navigate to detail page');
      return;
    }

    console.log('Navigating to detail page for asset:', asset.id, 'Type:', this.assetType);

    if (this.assetType === 'dataset') {
      const title = (asset as FeatureCardData).title ?? '';
      this.googleAnalytics.trackDatasetClick(title, asset.id);
      this.listingState.markPendingRestore('datasets');
      void navigateToCatalogueAssetDetail(this.router, 'dataset', asset.id);
    } else if (this.assetType === 'model') {
      const title = (asset as ModelCardData).title ?? '';
      this.googleAnalytics.trackModelClick(title, asset.id);
      this.listingState.markPendingRestore('models');
      void navigateToCatalogueAssetDetail(this.router, 'model', asset.id);
    } else if (this.assetType === 'usecase') {
      this.router.navigate(['/usecase', asset.id]);
    }
  }

  getDisplayTag(tag: string): string {
    // Replace PII with Personal for UI display only (case-insensitive)
    if (!tag) return tag;
    return tag.toUpperCase() === 'PII' ? 'Personal' : tag;
  }

  /**
   * Extract asset (catalogue item) from a user interaction API item.
   * The interaction API may return full asset info in assetDetails, item, asset, or the item itself.
   */
  private getAssetFromInteractionItem(item: any): any | null {
    if (!item || typeof item !== 'object') return null;
    const asset = item.assetDetails ?? item.item ?? item.asset ?? item;
    if (!asset || typeof asset !== 'object') return null;
    const hasId = asset.id != null || asset.itemId != null || asset.assetId != null || asset.entityId != null;
    const hasName = (asset.name != null && asset.name !== '') || (asset.label != null && asset.label !== '') || (asset.title != null && asset.title !== '');
    if (hasId || hasName) return asset;
    return null;
  }

  /**
   * Extract items array from API response
   */
  private extractItemsFromResponse(response: any): any[] {
    // Handle null/undefined response
    if (!response) {
      console.warn('Response is null or undefined');
      return [];
    }

    console.log('Extracting items from response. Response keys:', Object.keys(response));

    // Direct array response
    if (Array.isArray(response)) {
      console.log('Response is direct array, length:', response.length);
      return response;
    }

    // Check for result array (most common structure: { result: [...], paginationInfo: {...} })
    if (response.result) {
      console.log('Found response.result:', response.result);
      if (Array.isArray(response.result)) {
        console.log('response.result is array, length:', response.result.length);
        return response.result;
      }
      // Handle nested result.results
      if (response.result.results && Array.isArray(response.result.results)) {
        console.log('Found response.result.results, length:', response.result.results.length);
        return response.result.results;
      }
    }

    // Check for data array
    if (response.data && Array.isArray(response.data)) {
      console.log('Found response.data array, length:', response.data.length);
      return response.data;
    }

    // Check for items array
    if (response.items && Array.isArray(response.items)) {
      console.log('Found response.items array, length:', response.items.length);
      return response.items;
    }

    // Log warning if no items found
    console.warn('No items array found in response. Response structure:', Object.keys(response));
    console.warn('Full response:', JSON.stringify(response, null, 2));
    return [];
  }

  /**
   * Map API item to card data format based on asset type
   */
  private mapApiItemToCardData(item: any): FeatureCardData | ModelCardData | UsecaseCardData {
    if (this.assetType === 'dataset') {
      return this.mapApiItemToFeatureCard(item);
    } else if (this.assetType === 'model') {
      return this.mapApiItemToModelCard(item);
    } else {
      return this.mapApiItemToUsecaseCard(item);
    }
  }

  /**
   * Map API item to FeatureCardData format (for datasets)
   */
  private mapApiItemToFeatureCard(item: any): FeatureCardData {
    return this.featureCardMapper.mapApiItemToFeatureCard(item);
  }

  /**
   * Map API item to ModelCardData format (for models)
   */
  private mapApiItemToModelCard(item: any): ModelCardData {
    const id = item.id || '';
    const title = item.label || item.name || 'Untitled';
    const organization = item.organizationName || item.provider || item.organisation || item.organization || item.provider_name || '';
    const rawDesc =
      item.shortDescription ??
      item.short_description ??
      item.sortDescription ??
      item.description ??
      '';
    const shortDescription = typeof rawDesc === 'string' && rawDesc.trim()
      ? (rawDesc.length > 120 ? rawDesc.trim().slice(0, 120) + '…' : rawDesc.trim())
      : undefined;

    // Extract tags from API
    const apiTags: string[] = [];
    if (item.tags && Array.isArray(item.tags)) {
      apiTags.push(...item.tags);
    }

    const industry = this.helperService.extractIndustry(item);

    const modelType = item.modelType || '';
    const language = item.codeLanguage || item.programmingLanguage || item.language || '';
    const fileFormat = item.fileFormat || item.format || '';
    const date = this.formatService.formatDateForModelCard(
      item.lastUpdated || item.itemCreatedAt || item.createdAt || new Date()
    );
    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);
    const link = `/model/${id}`;

    // Extract metrics data from metrics object
    const metricsData = item.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item.likeCount ?? item.likes ?? item.like_count ?? 0,
      downloads: metricsData.downloads ?? item.downloadCount ?? item.downloads ?? item.download_count ?? 0,
      files: item.fileCount || item.files || item.file_count || 0,
      views: metricsData.views ?? item.viewCount ?? item.views ?? item.view_count ?? 0,
      dislikes: metricsData.dislikes ?? 0
    };

    return {
      id,
      title,
      organization,
      shortDescription,
      industry,
      tags: apiTags.slice(0, 3),
      modelType,
      language,
      fileFormat: fileFormat || undefined,
      date,
      badge,
      badgeColor,
      link,
      metrics: metrics
    };
  }

  /**
   * Map API item to UsecaseCardData format (for usecases)
   */
  private mapApiItemToUsecaseCard(item: any): UsecaseCardData {
    const id = item.id || '';
    const title = item.label || item.name || 'Untitled';
    // Support multiple API field names (interaction API may use shortDescription, sortDescription, etc.)
    const description =
      (item.description ?? item.shortDescription ?? item.short_description ?? item.longDescription ?? item.long_description ?? item.sortDescription ?? '') || '';

    // Extract tags from API
    const apiTags: string[] = [];
    if (item.tags && Array.isArray(item.tags)) {
      apiTags.push(...item.tags);
    }

    const date = this.formatService.formatDateForCard(item.itemCreatedAt || item.createdAt || new Date());
    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);
    const isNew = this.isRecentItem(item.itemCreatedAt || item.createdAt);
    const link = `/usecase/${id}`;

    // Extract metrics data from metrics object
    const metricsData = item.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item.likeCount ?? item.likes ?? item.like_count ?? 0,
      downloads: metricsData.downloads ?? item.downloadCount ?? item.downloads ?? item.download_count ?? 0,
      files: item.fileCount || item.files || item.file_count || 0,
      views: metricsData.views ?? item.viewCount ?? item.views ?? item.view_count ?? 0,
      dislikes: metricsData.dislikes ?? 0
    };

    return {
      id,
      title,
      description,
      tags: apiTags.slice(0, 4),
      apiTags: apiTags,
      badge,
      badgeColor,
      isNew,
      link,
      metrics: metrics
    };
  }

  /**
   * Check if item was created within last 7 days
   */
  private isRecentItem(dateString: string | Date): boolean {
    if (!dateString) return false;
    const itemDate = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return itemDate >= weekAgo;
  }

}

